<template>
  <v-app :class="theme">
    <a
      href="#main-content"
      class="skip-link"
    >
      Skip to main content
    </a>

    <Header
      v-if="isUserSignedIn"
      ref="header"
      :can-resume-tour="canResumeCurrentTour"
      :class="theme"
      :page-help-path="currentPageHelpPath"
      @show-keyboard-shortcuts="showKeyboardShortcuts = true"
      @resume-tour="resumeCurrentTour"
      @start-tour="startCurrentTour"
    />

    <Navigation
      v-if="isUserSignedIn"
      :class="theme"
    />

    <v-main
      id="main-content"
      :class="theme"
      tabindex="-1"
      aria-label="Main content"
    >
      <PrivacyPolicy v-if="isPrivacyPolicyRoute" />

      <TermsOfService v-else-if="isTermsOfServiceRoute" />

      <Welcome v-else-if="showSignIn && isRootRoute" />

      <Auth v-else-if="showSignIn" />

      <router-view
        v-else-if="isUserSignedIn && isValidRoute"
        class="fill-height"
      />

      <v-card
        v-else-if="isUserSignedIn && !isValidRoute"
        ref="not-found-card"
        data-elementName="NotFoundCard"
        class="ma-3"
      >
        <v-card-text>
          <div class="d-flex align-center">
            <span class="mr-2 font-weight-bold">404</span>
            <span class="text-body-1">{{ invalidRouteReason || 'Not Found' }}</span>
          </div>
        </v-card-text>
      </v-card>

      <v-progress-circular
        v-else-if="isAuthCheckRequested || isAutoAuthing || isSigningIn"
        class="ma-3"
        indeterminate
        color="primary"
      />

      <error-card
        v-if="promptError"
        class="ma-3"
        data-elementName="ErrorCard"
      />

      <v-snackbar
        v-model="notification"
      >
        <v-row
          no-gutters
          class="align-center"
        >
          <v-col class="py-0">
            <!-- @todo Sanitize message just in case. -->
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span>{{ snack.message }}</span>

            <!-- Show action text if action exists -->
            <v-btn
              v-if="snack.action && snack.action.name"
              color="primary"
              style="margin-top: -3px; margin-left: -3px;"
              text
              @click="fireActionEvent(snack)"
            >
              {{ snack.action.name }}
            </v-btn>
          </v-col>

          <v-col class="text-right col-auto py-0 pl-4">
            <!-- Close button -->
            <v-btn
              icon
              @click="notification = false"
            >
              <font-awesome-icon
                :icon="['fas', 'times']"
                size="lg"
              />
            </v-btn>
          </v-col>
        </v-row>
      </v-snackbar>

      <toast-stack />

      <feature-modal v-if="isUserSignedIn" />
      <settings-modal v-if="isUserSignedIn" />
      <refresh-modal v-if="isUserSignedIn" />

      <!-- Context-aware Keyboard Shortcuts Modal -->
      <v-dialog
        v-if="isUserSignedIn"
        v-model="showKeyboardShortcuts"
        max-width="600"
      >
        <v-card>
          <v-card-title class="headline primary white--text">
            <font-awesome-icon
              :icon="['fas', 'keyboard']"
              class="mr-2"
              size="lg"
            />
            Keyboard Shortcuts » {{ currentPageTitle }}
          </v-card-title>

          <v-card-text class="pt-4">
            <!-- General Shortcuts -->
            <div class="overline mb-2">
              General
            </div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      Action
                    </th>
                    <th class="text-left">
                      Shortcut
                    </th>
                    <th class="text-left">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Show Shortcuts</td>
                    <td>
                      <v-chip
                        small
                        outlined
                      >
                        ?
                      </v-chip>
                    </td>
                    <td class="caption">
                      Open this keyboard shortcuts panel
                    </td>
                  </tr>
                  <tr>
                    <td>Focus Search</td>
                    <td>
                      <v-chip
                        small
                        outlined
                      >
                        /
                      </v-chip>
                    </td>
                    <td class="caption">
                      Move focus to the search bar
                    </td>
                  </tr>
                  <tr>
                    <td>Toggle Dark Mode</td>
                    <td>
                      <v-chip
                        small
                        outlined
                        class="mr-1"
                      >
                        {{ modKey }}+'
                      </v-chip>
                    </td>
                    <td class="caption">
                      Switch between light and dark themes
                    </td>
                  </tr>
                  <tr>
                    <td>Toggle Settings</td>
                    <td>
                      <v-chip
                        small
                        outlined
                      >
                        {{ modKey }}+,
                      </v-chip>
                    </td>
                    <td class="caption">
                      Open or close the settings panel
                    </td>
                  </tr>
                  <tr>
                    <td>Close Dialogs</td>
                    <td>
                      <v-chip
                        small
                        outlined
                      >
                        Esc
                      </v-chip>
                    </td>
                    <td class="caption">
                      Dismiss open dialogs or modals
                    </td>
                  </tr>
                  <tr>
                    <td>Start Tour</td>
                    <td>
                      <v-chip
                        small
                        outlined
                      >
                        T
                      </v-chip>
                    </td>
                    <td class="caption">
                      Start the guided tour for this view when available
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <!-- Page-Specific Shortcuts -->
            <div
              v-if="currentRoute.name === 'time'"
              class="mt-4"
            >
              <div class="overline mb-2">
                Time View
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        Action
                      </th>
                      <th class="text-left">
                        Shortcut
                      </th>
                      <th class="text-left">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>New Time Entry</td>
                      <td>
                        <v-chip
                          small
                          outlined
                        >
                          N
                        </v-chip>
                      </td>
                      <td class="caption">
                        Open the new time entry form
                      </td>
                    </tr>
                    <tr>
                      <td>Multi-Select</td>
                      <td>
                        <v-chip
                          small
                          outlined
                        >
                          {{ modKey }}+Click
                        </v-chip>
                      </td>
                      <td class="caption">
                        Select multiple time entries at once
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              text
              @click="showKeyboardShortcuts = false"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>

    <!-- Global Tour System -->
    <TourSystem />
  </v-app>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import PATHS from 'src/router/paths'
import { fireanalytics } from 'src/store/firebase'
import tourManager from '@/dashboard/utils/tourManager'
import themeClasses from '@/mixins/themeClasses'

const {
  mapGetters: mapAuthGetters,
  mapState: mapAuthState,
} = createNamespacedHelpers('auth')
const { mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Layout',
  components: {
    Auth: () => import('src/auth/views/Auth'),
    Welcome: () => import('src/auth/views/Welcome'),
    PrivacyPolicy: () => import('src/docs/views/PrivacyPolicy'),
    TermsOfService: () => import('src/docs/views/TermsOfService'),
    ErrorCard: () => import('../components/ErrorCard'),
    Header: () => import('../components/Header'),
    Navigation: () => import('../components/Navigation'),
    FeatureModal: () => import('../components/FeatureModal'),
    RefreshModal: () => import('../components/RefreshModal'),
    SettingsModal: () => import('../components/SettingsModal'),
    ToastStack: () => import('src/tools/components/ToastStack'),
    TourSystem: () => import('src/dashboard/components/TourSystem'),
  },
  mixins: [themeClasses],
  data() {
    return {
      showKeyboardShortcuts: false,
    }
  },
  computed: {
    ...mapAuthGetters([
      'inDarkMode',
      'isActiveUser',
      'isUserSignedIn',
      'showSignIn',
    ]),
    ...mapAuthState({
      demoMode: (state) => state.demoMode,
      isAutoAuthing: (state) => state.isAutoAuthing,
      isAuthCheckRequested: (state) => state.isAuthCheckRequested,
      isSigningIn: (state) => state.isSigningIn,
      user: (state) => state.user,
    }),
    ...mapLayoutState({
      currentRoute: (state) => state.currentRoute,
      darkMode: (state) => state.darkMode,
      promptError: (state) => state.promptError,
    }),
    ...mapToolsGetters(['isValidRoute', 'organizations', 'reportKey']),
    ...mapToolsState({
      reportId: (state) => state.reportId,
      reportName: (state) => state.reportName,
      searchResults: (state) => state.searchResults,
      snack: (state) => state.snack,
      snackbar: (state) => state.snackbar,
      subId: (state) => state.subId,
    }),
    currentPageTitle() {
      return this.reportName || 'General'
    },
    modKey() {
      return navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'
    },
    notification: {
      get() {
        return this.snackbar
      },
      set(value) {
        this.$store.commit('tools/snackbar', value, { root: true })
      },
    },
    customReportOptions() {
      return this.searchResults.endpoints['custom-reports']?.items?.list || []
    },
    theme() {
      const surface = this.themeSurfaceColor('icewhite', 'black')
      const text = this.themeTextClass('black--text', 'white--text')
      return `${surface} ${text}`
    },
    isPrivacyPolicyRoute() {
      // Reachable with or without a signed-in session - the OAuth consent
      // screen's registered privacy policy URL must work for signed-out
      // visitors (including Google's own review crawler), and existing
      // signed-in users linking to it shouldn't hit a 404 either.
      return this.$route.name === 'privacy-policy'
    },
    isTermsOfServiceRoute() {
      // Same reasoning as isPrivacyPolicyRoute. TermsOfService.vue itself
      // carries a prominent "DRAFT - Pending Legal Review" banner, so
      // being reachable here doesn't present it as approved or binding.
      return this.$route.name === 'terms-of-service'
    },
    isRootRoute() {
      // Only the bare "/" home path shows the informational Welcome view
      // for signed-out visitors - deep links (e.g. /sign-in, /admin/users,
      // a password reset link) should still go straight to the relevant
      // Auth flow, not an intermediate landing page.
      return this.$route.name === 'dashboard'
    },
    invalidRouteReason() {
      if (this.isValidRoute) return null

      const { $route, $store } = this
      const { name, params, query } = $route

      // Check selectedSearch state first (more specific)
      const selectedSearch = $store.state.tools?.selectedSearch

      if (selectedSearch?.isSearchOption === false) {
        return selectedSearch?.text || 'Selection Not Found'
      }

      // Check for missing reportKey (most common case)
      if (!this.reportKey) {
        // If we have a valid selectedSearch, check Router state
        if (selectedSearch?.reportKey) {
          // Getters now read from Router.currentRoute directly
          const currentRouteName = this.$route?.name
          const currentReportName = this.$route?.params?.reportName

          if (!currentRouteName && !currentReportName) {
            return `Router not ready (selectedSearch has reportKey: "${selectedSearch.reportKey}", but $route is empty)`
          }

          return `Router has route but reportKey getter returning empty (selectedSearch.reportKey: "${selectedSearch.reportKey}", $route.name: "${currentRouteName}")`
        }

        // No selectedSearch at all
        const hasRouteParams = params?.reportName || params?.reportPath
        const hasQueryParams = Object.keys(query || {}).length > 0

        if (hasRouteParams) {
          return `No selectedSearch found for route params: ${params.reportName || params.reportPath}`
        }

        if (hasQueryParams) {
          return `No selectedSearch found for query: ${JSON.stringify(query)}`
        }

        return `No route data found (route name: ${name || 'unknown'})`
      }

      return 'Route configuration error'
    },

    currentTourId() {
      const reportName = this.$route?.params?.reportName

      if (this.reportKey === 'time' || reportName === 'time') {
        return 'time-entry-quick-start'
      }

      if (this.reportKey === 'admin' && reportName === 'projects') {
        return 'admin-projects-intro'
      }

      if (this.reportKey === 'projects' || reportName === 'projects') {
        return 'projects-dashboard-overview'
      }

      return null
    },

    canResumeCurrentTour() {
      if (!this.currentTourId) return false
      return tourManager.canResumeTour(this.currentTourId)
    },

    currentPageHelpPath() {
      const reportName = this.$route?.params?.reportName
      const reportId = this.$route?.params?.reportId

      if (this.reportKey === 'time' || reportName === 'time') {
        return '/docs/guides#time'
      }

      if (this.reportKey === 'admin' && reportName === 'projects') {
        return '/docs/guides#admin'
      }

      if (this.reportKey === 'projects' || reportName === 'projects') {
        return '/docs/guides#projects'
      }

      if (this.reportKey === 'organization' || reportName === 'organization') {
        return '/docs/guides#organization'
      }

      if (this.reportKey === 'person' || reportName === 'person') {
        return '/docs/guides#person'
      }

      if (this.reportKey === 'pacing' || reportName === 'pacing') {
        return '/docs/guides#pacing'
      }

      if (this.reportKey === 'discrepancy' || reportName === 'discrepancy') {
        return '/docs/guides#discrepancy'
      }

      if (reportName === 'docs' || reportId === 'release-notes') {
        return '/docs/release-notes'
      }

      return '/docs/getting-started'
    },
  },
  watch: {
    async $route(to, from) {
      // @todo Restore document title setter.
      this.setTitle(to, from || {})
    },
    inDarkMode(newTheme) {
      this.updateTheme(newTheme ? 'dark' : 'light')
    },
    isUserSignedIn(newValue) {
      // Pre-auth-only routes (the sign-in form, registration, password
      // reset/forgot flows) aren't real in-app destinations - completing
      // sign-in while sitting on one would otherwise leave the user
      // stranded on a "404 Not Found" card instead of landing on their
      // dashboard. This was never an issue before "/sign-in" existed as
      // its own route, since the sign-in form always used to render at
      // "/" itself.
      const preAuthOnlyRouteNames = ['sign-in', 'sign-up', 'forgot-password', 'reset-password']
      if (newValue && preAuthOnlyRouteNames.includes(this.$route.name)) {
        this.$router.push('/')
      }
    },
  },
  mounted() {
    // Add global keyboard shortcuts
    window.addEventListener('keydown', this.handleGlobalKeyboardShortcut)
  },
  beforeDestroy() {
    // Remove global keyboard shortcuts
    window.removeEventListener('keydown', this.handleGlobalKeyboardShortcut)
  },
  methods: {
    /**
     * Handle global keyboard shortcuts
     * @param {KeyboardEvent} event - Keyboard event
     */
    handleGlobalKeyboardShortcut(event) {
      // Don't trigger shortcuts if user is typing in an input field
      const isInInputField = event.target.tagName === 'INPUT'
                            || event.target.tagName === 'TEXTAREA'
                            || event.target.isContentEditable

      // "/" key for focus search (unless already in input)
      if (event.key === '/' && !isInInputField) {
        event.preventDefault()
        this.focusSearchBar()
      } else if (event.key === '?' && !isInInputField) {
        // "?" key for show keyboard shortcuts (unless in input)
        event.preventDefault()
        this.showKeyboardShortcuts = true
      } else if (event.key.toLowerCase() === 't' && !isInInputField) {
        // "T" key for starting guided tours on supported views
        event.preventDefault()
        this.startCurrentTour('keyboard')
      } else if (event.key === 'Escape' && this.showKeyboardShortcuts) {
        // "Escape" key to close keyboard shortcuts modal
        event.preventDefault()
        this.showKeyboardShortcuts = false
      } else if (event.key === "'" && (event.ctrlKey || event.metaKey) && !event.shiftKey && !isInInputField) {
        // Ctrl/Cmd+' for dark mode toggle
        event.preventDefault()
        this.toggleDarkMode()
      } else if (event.key === ',' && (event.ctrlKey || event.metaKey) && !isInInputField) {
        // Ctrl/Cmd+, to toggle settings
        event.preventDefault()
        const current = this.$store.state.layout.isSettingsModalVisible
        this.$store.commit('layout/isSettingsModalVisible', !current, { root: true })
      }
    },

    /**
     * Focus the search bar in the header
     */
    focusSearchBar() {
      if (this.$refs.header && this.$refs.header.focusSearch) {
        this.$refs.header.focusSearch()
      } else {
        // Fallback: try to find search input via DOM query
        const searchInput = document.querySelector('input[prepend-inner-icon="search"]')
                           || document.querySelector('.client-search input')
                           || document.querySelector('.v-text-field input[type="text"]')

        if (searchInput) {
          searchInput.focus()
        }
      }
    },

    updateTheme(newTheme) {
      this.$store.dispatch('layout/setDarkMode', newTheme === 'dark')
      this.$vuetify.theme.dark = newTheme === 'dark'
    },

    toggleDarkMode() {
      const newValue = !this.inDarkMode
      const themes = { activeTheme: newValue ? 'dark' : 'light' }
      this.$store.dispatch('auth/updateDarkMode', themes, { root: true })
      this.$store.dispatch('layout/setDarkMode', newValue, { root: true })
      this.$vuetify.theme.dark = newValue
    },

    startCurrentTour(source = 'header') {
      if (!this.isValidRoute) {
        return
      }

      if (!this.currentTourId) {
        fireanalytics.event('tour_unavailable', {
          reportKey: this.reportKey || 'unknown',
          routeName: this.$route?.name || 'unknown',
          source,
        })

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'No guided tour is available for this view yet. Open Page Guide from Help for route-specific steps.',
          status: 'info',
        }, { root: true })
        return
      }

      fireanalytics.event('tour_start', {
        reportKey: this.reportKey || 'unknown',
        routeName: this.$route?.name || 'unknown',
        source,
        tourId: this.currentTourId,
      })

      this.$root.$emit('tour:start', this.currentTourId)
    },

    resumeCurrentTour(source = 'header') {
      if (!this.isValidRoute || !this.currentTourId) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'No guided tour is available for this view yet. Open Page Guide from Help for route-specific steps.',
          status: 'info',
        }, { root: true })
        return
      }

      if (!tourManager.canResumeTour(this.currentTourId)) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'No saved tour progress was found for this view. Starting from step 1.',
          status: 'info',
        }, { root: true })
        this.startCurrentTour(source)
        return
      }

      fireanalytics.event('tour_resume', {
        reportKey: this.reportKey || 'unknown',
        routeName: this.$route?.name || 'unknown',
        source,
        tourId: this.currentTourId,
      })

      this.$root.$emit('tour:resume', this.currentTourId)
    },

    fireActionEvent(event) {
      const action = event && event.action
      const isValidAction = action
        && action.type === 'internal/goToProject'
        && action.name === 'View Project'
        && action.data
        && typeof action.data === 'object'
        && !Array.isArray(action.data)

      if (!isValidAction) {
        this.$log.warn(['Layout', 'No valid action defined for snackbar event:', event])
        return
      }

      this.$store.dispatch('internal/goToProject', action.data)
    },
    setTitle(to, from) {
      const titleMap = {
        admin: () => 'Admin | iAdOps',
        dashboard: () => 'Dashboard | iAdOps',
        docs: () => 'Docs | iAdOps',
        home: () => 'iAdOps',
        settings: () => 'Settings | iAdOps',

        [PATHS.REPORTS.DISCREPANCY]: (itemLabel) => `${itemLabel || 'All'} | Discrepancy`,
        [PATHS.REPORTS.PACING]: (itemLabel) => `${itemLabel || 'All'} | Pacing`,
        [PATHS.REPORTS.ADX_HEALTH]: (itemLabel) => `${itemLabel || 'All'} | AdX Health`,
        [PATHS.REPORTS.NETWORK_HEALTH]: (itemLabel) => `${itemLabel || 'All'} | Network Health`,
        [PATHS.REPORTS.YIELD_PARTNER]: (itemLabel) => `${itemLabel || 'All'} | AdX & OB`,

        time: (itemLabel) => `${itemLabel || 'All'} | Time`,

        organization: (itemLabel) => `${itemLabel || 'All'} | Organization`,
        person: (itemLabel) => `${itemLabel || 'All'} | Person`,
        project: (itemLabel) => `${itemLabel || 'All'} | Project`,
      }

      const { isUserSignedIn, reportKey } = this
      const toParams = to?.params || this
      const fromParams = from?.params || this
      const setReportId = (rid, sid) => (sid && sid !== '-1' && `${rid}/${sid}`) || `${rid}`

      const { reportId: toReportId, reportName: toReportName, reportPath: toReportPath, subId: toSubId } = toParams
      const toRouteName = to?.name || ''
      const toIdentifier = (toReportId && setReportId(toReportId, toSubId)) || ''

      const getTitle = (toId) => {
        // Prioritize route params, then route name, then Vuex store state.
        // For the dashboard route ('/'), reportName is empty but route.name is 'dashboard'.
        const titleKey = toReportName || toRouteName || reportKey || 'dashboard'

        // Only look up org name when we have a specific reportId (detail view)
        // For dashboard/list views (no reportId), just use the default title
        const hasSpecificItem = !!toReportId && toReportId.length > 0
        const foundOrg = hasSpecificItem && this.organizations && this.organizations.find((org) => org.reportIds.indexOf(toReportId) > -1)
        const identifierForPageTitle = (foundOrg && foundOrg.clientName) || (hasSpecificItem ? toId : '')

        const isCustomReportRoute = this.$route.name === 'custom-reports'
        const findCustomReportConfig = () => {
          const findCustomReport = () => {
            const customReportPermissions = foundOrg && foundOrg.access?.permissions?.customReports
            return Array.isArray(customReportPermissions) && customReportPermissions.find((customReport) => customReport.href === toReportName)
          }
          return isCustomReportRoute && findCustomReport()
        }
        const getCustomReportTitle = (customReport) => {
          const getPageTitleForList = () => !customReport && 'All | Custom Reports'
          const getPageTitleForReport = () => customReport && customReport.name && `${customReport.name} | Custom Report`
          return isCustomReportRoute && (getPageTitleForList() || getPageTitleForReport() || 'Unknown | Custom Report')
        }
        const makePageTitle = (pageId) => {
          switch (titleKey) {
            case 'admin':
              return titleMap.admin(pageId)
            case 'dashboard':
              return titleMap.dashboard(pageId)
            case 'docs':
              return titleMap.docs(pageId)
            case 'home':
              return titleMap.home(pageId)
            case 'settings':
              return titleMap.settings(pageId)
            case PATHS.REPORTS.DISCREPANCY:
              return titleMap[PATHS.REPORTS.DISCREPANCY](pageId)
            case PATHS.REPORTS.PACING:
              return titleMap[PATHS.REPORTS.PACING](pageId)
            case PATHS.REPORTS.ADX_HEALTH:
              return titleMap[PATHS.REPORTS.ADX_HEALTH](pageId)
            case PATHS.REPORTS.NETWORK_HEALTH:
              return titleMap[PATHS.REPORTS.NETWORK_HEALTH](pageId)
            case PATHS.REPORTS.YIELD_PARTNER:
              return titleMap[PATHS.REPORTS.YIELD_PARTNER](pageId)
            case 'time':
              return titleMap.time(pageId)
            case 'organization':
              return titleMap.organization(pageId)
            case 'person':
              return titleMap.person(pageId)
            case 'project':
              return titleMap.project(pageId)
            default:
              return null
          }
        }
        return makePageTitle(identifierForPageTitle) || getCustomReportTitle(findCustomReportConfig()) || 'iAdOps'
      }
      // Privacy Policy / Terms of Service are reachable regardless of auth
      // state (see isPrivacyPolicyRoute/isTermsOfServiceRoute) - title them
      // from their own route meta rather than falling through to "Sign In".
      const isPublicLegalRoute = ['privacy-policy', 'terms-of-service'].includes(toRouteName)
      // Signed-out visitors to the bare root path see the Welcome view, not
      // the Sign In form - title it as the app name rather than "Sign In".
      const isSignedOutOnRootRoute = !isUserSignedIn && toRouteName === 'dashboard'
      const page_title = (isUserSignedIn && getTitle(toIdentifier))
        || (isPublicLegalRoute && `${to.meta.title} | iAdOps`)
        || (isSignedOutOnRootRoute && 'iAdOps')
        || 'Sign In | iAdOps'

      document.title = page_title

      const { location: { href: page_location, pathname: page_path } } = document
      const { reportId: fromReportId, reportName: fromReportName, reportPath: fromReportPath, subId: fromSubId } = fromParams
      const fromIdentifier = (fromReportId && setReportId(fromReportId, fromSubId)) || ''

      const fromPath = `${fromReportName || ''}/${(fromReportName && fromReportPath && `${fromReportPath}/`) || ''}${fromIdentifier}`
      const toPath = `${toReportName || ''}/${(toReportName && toReportPath && `${toReportPath}/`) || ''}${toIdentifier}`

      fireanalytics.event('page_view', {
        page_location,
        page_path,
        page_title,
      })

      fireanalytics.event('navigation', {
        category: 'Navigation',
        description: 'Update Route',
        label: `Update Route from "${fromPath}" to "${toPath}"`,
        pageLocation: page_location,
        pagePath: page_path,
        pageTitle: page_title,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

main {
  background-color: $color-icewhite;
}

.skip-link {
  left: 12px;
  top: 12px;
  z-index: 1000;
  position: absolute;
  transform: translateY(-200%);
  transition: transform 0.2s ease;
  background: #ffffff;
  border: 2px solid #005fcc;
  color: #003e8c;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
}

.skip-link:focus {
  transform: translateY(0);
}
</style>
